import { Autocomplete, TextField } from '@mui/material'
import IntakeCard from 'candidate/components/IntakeCard'
import { QuestionWrapper } from 'candidate/components/QuestionWrapper'
import { ChoiceBucket } from 'candidate/models/intake'
import { useState } from 'react'
import NextButton from 'shared/components/NextButton'

import type { Dispatch, FC } from 'react'

const OTHER_CHOICE = 'Other'

interface Props {
    internalName: string
    answer?: ChoiceBucket
    callback: Dispatch<any>
    choices: ChoiceBucket[]
    prompt: string
    displayName: string
    subtext?: string
    limit: number
    label?: string
    repeat?: number
    allowOther?: boolean
}

const DropdownMultiSelectQuestion: FC<Props> = ({
    internalName,
    answer,
    callback,
    choices,
    prompt,
    subtext,
    limit,
    label,
    repeat = 1,
    allowOther = false,
}: Props) => {
    const [formData, setFormData] = useState(answer || {})
    const [otherValues, setOtherValues] = useState<Record<string, string>>(
        () => {
            if (!answer?.[internalName]) return {}
            const initial: Record<string, string> = {}
            for (const [fieldName, selections] of Object.entries(
                answer[internalName] as Record<string, any[]>
            )) {
                for (const item of selections) {
                    if (item.customValue) initial[fieldName] = item.customValue
                }
            }
            return initial
        }
    )

    const handleSubmit = () => {
        let data = formData[internalName] || []

        if (allowOther) {
            const merged = { ...data }
            for (const fieldName in merged) {
                merged[fieldName] = merged[fieldName].map((item) => {
                    if (
                        item.choice === OTHER_CHOICE &&
                        otherValues[fieldName]
                    ) {
                        return { ...item, customValue: otherValues[fieldName] }
                    }
                    return item
                })
            }
            data = merged
        }

        const response = { [internalName]: data }
        callback({ response })
    }

    const onInputChange = ({ target }) => {
        const { name, value } = target

        if (value.length === 0) {
            delete formData[internalName][name]
            setFormData({ ...formData })
        } else {
            let tempObj = {
                [internalName]: {
                    ...formData[internalName],
                    [name]: value,
                },
            }
            setFormData({ ...formData, ...tempObj })
        }
    }

    /**
     * Determines if the given option should be disabled for the given field.
     */
    const isOptionDisabled = (
        option: ChoiceBucket,
        fieldName: string
    ): boolean => {
        if (!formData[internalName]) return false

        // Determine all currently selected choices across all fields so we can
        // prevent the user from selecting the same option in multiple fields.
        const allSelectedChoices = Object.entries<ChoiceBucket[]>(
            formData[internalName]
        ).flatMap(([, currentFieldSelections]) =>
            currentFieldSelections.map((cfs) => cfs.choice)
        )

        // Determine if the current field is at its maximum limit of selections.
        const isFieldAtLimit =
            formData[internalName][fieldName]?.length >= limit

        // Determine if the option is selected in the current field. If so, we
        // don't want to disable it or the user won't be able to deselect it
        // using the dropdown.
        const isOptionSelectedInField = formData[internalName][fieldName]?.some(
            (item) => item.choice === option.choice
        )

        if (option.choice === OTHER_CHOICE && allowOther) {
            return !isOptionSelectedInField && isFieldAtLimit
        }

        return (
            !isOptionSelectedInField &&
            (isFieldAtLimit || allSelectedChoices.includes(option.choice))
        )
    }

    const isButtonDisabled = (): boolean => {
        let formArr: string[] =
            formData[internalName] !== undefined
                ? Object.keys(formData[internalName])
                : []

        let hasAtLeastOneSelection = formArr.length > 0
        let filledAnswers = formArr.length === repeat
        return !hasAtLeastOneSelection && !filledAnswers
    }

    const hasOtherSelected = (fieldName: string): boolean => {
        return formData[internalName]?.[fieldName]?.some(
            (item) => item.choice === OTHER_CHOICE
        )
    }

    return (
        <IntakeCard question={prompt} subtext={subtext}>
            <QuestionWrapper>
                {Array.from({ length: repeat }, (_, index) => {
                    const fieldName = `${internalName}_${index}`
                    const newLabel =
                        repeat === 1
                            ? label
                            : ['First ', 'Second ', 'Third '][index] + label
                    return (
                        <div key={fieldName}>
                            <Autocomplete
                                multiple
                                fullWidth
                                id={`${fieldName}_label`}
                                isOptionEqualToValue={(option, value) =>
                                    option.choice === value.choice
                                }
                                onChange={(event, newValue) => {
                                    onInputChange({
                                        target: {
                                            name: fieldName,
                                            value: newValue,
                                        },
                                    })
                                    if (
                                        allowOther &&
                                        !newValue.some(
                                            (v) => v.choice === OTHER_CHOICE
                                        )
                                    ) {
                                        setOtherValues((prev) => {
                                            const next = { ...prev }
                                            delete next[fieldName]
                                            return next
                                        })
                                    }
                                }}
                                value={
                                    formData[internalName]?.[fieldName] || []
                                }
                                options={choices}
                                getOptionLabel={(option) => option.choice}
                                getOptionDisabled={(option) =>
                                    isOptionDisabled(option, fieldName)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={newLabel}
                                        placeholder=""
                                        inputProps={{
                                            ...params.inputProps,
                                        }}
                                    />
                                )}
                            />
                            {allowOther && hasOtherSelected(fieldName) && (
                                <TextField
                                    fullWidth
                                    label="Enter firm name"
                                    value={otherValues[fieldName] || ''}
                                    onChange={(e) =>
                                        setOtherValues((prev) => ({
                                            ...prev,
                                            [fieldName]: e.target.value,
                                        }))
                                    }
                                    sx={{ mt: 1 }}
                                />
                            )}
                        </div>
                    )
                })}
            </QuestionWrapper>

            <NextButton
                disabled={isButtonDisabled()}
                onClickHandler={handleSubmit}
            />
        </IntakeCard>
    )
}

export default DropdownMultiSelectQuestion
